import { useContext, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";

import { AuthButton, Authorization, Datas, TokenContext } from "../App";
import login_s from '../styles/Login.module.css';

const API_BASE = 'https://libervpn.ru/api';
// const API_BASE = 'http://localhost:5000';

interface LoginData {
  mail?: string;
  password?: string;
  username?: string;
}

interface CustomJwtPayload {
  [key: string]: unknown;
}

function Login() {
  const authContext = useContext(Authorization);
  const datasContext = useContext(Datas);
  const tokenContext = useContext(TokenContext);
  const authButtonContext = useContext(AuthButton);

  const login_ref = useRef<HTMLInputElement>(null);
  const password_ref = useRef<HTMLInputElement>(null);
  const email_ref = useRef<HTMLInputElement>(null);
  const register_password_ref = useRef<HTMLInputElement>(null);
  const register_password2_ref = useRef<HTMLInputElement>(null);
  const register_username_ref = useRef<HTMLInputElement>(null);
  const code_ref = useRef<HTMLInputElement>(null);
  const forgotCodeRef = useRef<HTMLInputElement>(null);
  const newPassword = useRef<HTMLInputElement>(null);
  const newPassword2 = useRef<HTMLInputElement>(null);
  const forgotEmailRef = useRef<HTMLInputElement>(null);

  const [regButton, setRegButton] = useState<boolean>(false);
  const [regError, setRegError] = useState<string>('');
  const [logError, setLogError] = useState<boolean>(false);
  const [usernameHint, setUsernameHint] = useState<string>('');
  const [emailHint, setEmailHint] = useState<string>('');
  const [passwordHint, setPasswordHint] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showRegPassword, setShowRegPassword] = useState<boolean>(false);
  const [showRegPassword2, setShowRegPassword2] = useState<boolean>(false);
  const [showConfirmCode, setShowConfirmCode] = useState<boolean>(false);
  const [confirmEmail, setConfirmEmail] = useState<string>(''); 
  const [code, setCode] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const [emptyFields, setEmptyFields] = useState<string[]>([]);
  const [loginData, setLoginData] = useState<LoginData>({});
  const [forgotEmail, setForgotEmail] = useState<string | undefined>('');

  const [forgotStep, setForgotStep] = useState<number>(0);
  const [newToken, setNewToken] = useState<string>('');
  const ref = window.localStorage.getItem('ref');

  const EyeOpen = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeClosed = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowed = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]$/;
    if (!allowed.test(e.key) && e.key.length === 1) e.preventDefault();
  };

  function validateUsername(val: string) {
    if (val.length > 0 && val.length < 4) {
      setUsernameHint('Слишком короткий');
    } else if (val.includes(' ')) {
      setUsernameHint('Логин не может содержать пробелы');
    } else if (val.includes(',') || val.includes('.')) {
      setUsernameHint('Логин не может содержать точки или запятые');
    } else {
      setUsernameHint('');
    }

    const login = email_ref.current?.value || '';
    const hasRussianChars = /[а-яА-ЯёЁ]/;
    const username = register_username_ref.current?.value || '';
    if (hasRussianChars.test(login) || hasRussianChars.test(username)) {
      setRegError('Использование русских символов запрещено');
    } else {
      setRegError('');
    }
  }

  function validatePassword(val: string) {
    if (val.length > 0 && val.length < 8) {
      setPasswordHint('Слишком короткий');
    } else {
      setPasswordHint('');
    }
  }

  function validateEmail(val: string) {
    if (val.length > 0 && !val.includes('@')) setEmailHint('Введите корректный email');
    else setEmailHint('');

    const login = email_ref.current?.value || '';
    const hasRussianChars = /[а-яА-ЯёЁ]/;
    const username = register_username_ref.current?.value || '';
    if (hasRussianChars.test(login) || hasRussianChars.test(username)) {
      setRegError('Использование русских символов запрещено');
    } else {
      setRegError('');
    }
  }

  async function handleRegister() {
    const mail = loginData.mail;
    const password = loginData.password;
    const username = loginData.username;
    const currentCode = code_ref.current?.value || '';

    try {
      const response = await fetch(`${API_BASE}/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mail, password, username, code: currentCode, ref })
      });

      const dataResponse = await response.json();
      if (response.ok) {
        setShowConfirmCode(false);
        authContext?.setAuth(true);
        tokenContext?.setToken(dataResponse['access_token']);
        datasContext?.setDatas(jwtDecode<CustomJwtPayload>(dataResponse['access_token']));
        authButtonContext?.setAuthBut(false);
      } else {
        setCodeError('Неверный код подтверждения');
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function handleAuthentication(mail: string | undefined) {
    try {
      if (mail?.includes('@') && mail.length > 0) {
        const response = await fetch(`${API_BASE}/authentication`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mail })
        });
        const resData = await response.json();
        console.log(resData);
        setForgotEmail(mail);
        console.log(mail);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function secForgotStep() {
    try {
      const response = await fetch(`${API_BASE}/check_forgot`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: forgotCodeRef.current?.value, mail: forgotEmail })
      });
      const resData = await response.json();
      setNewToken(resData?.token || '');
      if (response.ok) {
        setForgotStep(3);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function thirdForgotStep() {
    const pass1 = newPassword.current?.value || '';
    const pass2 = newPassword2.current?.value || '';
    if (pass1 !== pass2) {
      setPasswordHint('Пароли не совпадают');
      return;
    }
    setPasswordHint('');

    try {
      const response = await fetch(`${API_BASE}/new_password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'authorization': newToken
        },
        body: JSON.stringify({ password: pass1 })
      });

      const dataResponse = await response.json();
      if (response.ok) {
        authContext?.setAuth(true);
        tokenContext?.setToken(dataResponse['access_token']);
        datasContext?.setDatas(jwtDecode<CustomJwtPayload>(dataResponse['access_token']));
        authButtonContext?.setAuthBut(false);
      } else {
        setLogError(true);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function handleLogin() {
    const login = login_ref.current?.value || '';
    const password = password_ref.current?.value || '';

    const empty: string[] = [];
    if (!login) empty.push('login');
    if (!password) empty.push('password');
    if (empty.length > 0) {
      setEmptyFields(empty);
      setLogError(false);
      return;
    }
    setEmptyFields([]);

    try {
      const response = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: login.toLocaleLowerCase(), password })
      });
      const dataResponse = await response.json();
      if (response.ok) {
        authContext?.setAuth(true);
        tokenContext?.setToken(dataResponse['access_token']);
        datasContext?.setDatas(jwtDecode<CustomJwtPayload>(dataResponse['access_token']));
        authButtonContext?.setAuthBut(false);
      } else {
        setLogError(true);
      }
    } catch (err) {
      console.error(err);
    }
  }

  function resendCode() {
    handleAuthentication(loginData.mail);
  }

  function closeLogin() {
    authButtonContext?.setAuthBut(false);
    setShowConfirmCode(false);
    setForgotStep(0);
  }

  async function goToCode() {
    const mail = email_ref.current?.value || '';
    const password = register_password_ref.current?.value || '';
    const password2 = register_password2_ref.current?.value || '';   
    const hasRussianChars = /[а-яА-ЯёЁ]/;
    const username = register_username_ref.current?.value || '';
    const login = email_ref.current?.value || '';

    if (hasRussianChars.test(login) || hasRussianChars.test(username)) {
      return;
    }

    const empty: string[] = [];
    if (!mail) empty.push('email');
    if (!username) empty.push('username');
    if (!password) empty.push('reg_password');
    if (!password2) empty.push('reg_password2');

    if (empty.length > 0) {
      setEmptyFields(empty);
      setRegError('Заполните все поля');
      return;
    }

    if (username.includes(' ')) {
      setUsernameHint('Логин не может содержать пробелы');
      return;
    }

    if (username.includes(',') || username.includes('.')) {
      setUsernameHint('Логин не может содержать точки или запятые');
      return;
    }

    if (!mail.includes('@')) {
      setEmailHint('Введите корректный email с @');
      return;
    }

    if (password !== password2) {
      setPasswordHint('Пароли не совпадают');
      return;
    }

    if (username.length <= 4 || username.length >= 32) {
      setUsernameHint('Логин должен быть от 4 до 32 символов');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/check_register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mail, username })
      });

      if (response.status === 409) {
        const res_data = await response.json();
        if (res_data['error'] === 'EMAIL_TAKEN') setEmailHint('Этот email уже занят');
        if (res_data['error'] === 'LOGIN_TAKEN') setUsernameHint('Этот логин уже занят');
        return;
      }

      setEmptyFields([]);
      setEmailHint('');
      setPasswordHint('');
      setLoginData({ password, mail, username });
      setConfirmEmail(mail);
      await handleAuthentication(mail);
      setShowConfirmCode(true);

    } catch (err) {
      console.error(err);
    }
  }

  function handleFormClick(e: React.MouseEvent) {
    e.stopPropagation();
  }

  return (
    <div className={login_s.background_form} onClick={closeLogin}>
      <div className={login_s.form} onClick={handleFormClick}>
        {forgotStep === 1 ? (
          <>
            <button className={login_s.close_btn} onClick={closeLogin}>✕</button>
            <h2 className={login_s.title}>Восстановление пароля</h2>
            <div className={login_s.inputs_wrapper}>
              <label>Почта</label>
              <div className={login_s.input_wrapper}>
                <input
                  type="text"
                  placeholder="Введите email"
                  ref={forgotEmailRef}
                  onChange={(e) => validateEmail(e.target.value)}
                />
              </div>
              {emailHint && <p className={login_s.hint}>⚠ {emailHint}</p>}
              <button
                onClick={() => {
                  setForgotStep(2);
                  handleAuthentication(forgotEmailRef.current?.value.toLocaleLowerCase());
                }}
                className={login_s.submit_but}
              >
                Дальше
              </button>
            </div>
          </>
        ) : forgotStep === 2 ? (
          <>
            <button className={login_s.close_btn} onClick={closeLogin}>✕</button>
            <h2 className={login_s.title}>Ввод кода</h2>
            <p className={login_s.subtitle}>Введите код, отправленный на вашу почту</p>
            <div className={login_s.inputs_wrapper}>
              <label>Код из письма</label>
              <div className={login_s.input_wrapper}>
                <input
                  type="text"
                  placeholder="Введите код"
                  maxLength={4}
                  ref={forgotCodeRef}
                />
              </div>
              <button onClick={secForgotStep} className={login_s.submit_but}>Дальше</button>
            </div>
          </>
        ) : forgotStep === 3 ? (
          <>
            <button className={login_s.close_btn} onClick={closeLogin}>✕</button>
            <h2 className={login_s.title}>Новый пароль</h2>
            <div className={login_s.inputs_wrapper}>
              <label>Новый пароль</label>
              <div className={login_s.input_wrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Придумайте пароль"
                  ref={newPassword}
                />
                <button className={login_s.eye_btn} onClick={() => setShowPassword(p => !p)}>
                  {showPassword ? <EyeClosed /> : <EyeOpen />}
                </button>
              </div>

              <label>Повторите пароль</label>
              <div className={login_s.input_wrapper}>
                <input
                  type={showRegPassword2 ? "text" : "password"}
                  placeholder="Повторите пароль"
                  ref={newPassword2}
                />
                <button className={login_s.eye_btn} onClick={() => setShowRegPassword2(p => !p)}>
                  {showRegPassword2 ? <EyeClosed /> : <EyeOpen />}
                </button>
              </div>
              {passwordHint && <p className={login_s.error}>⚠ {passwordHint}</p>}
              <button onClick={thirdForgotStep} className={login_s.submit_but}>Сохранить и войти</button>
            </div>
          </>
        ) : !showConfirmCode ? (
          <>
            <button className={login_s.close_btn} onClick={closeLogin}>✕</button>
            <h2 className={login_s.title}>{!regButton ? 'Вход' : 'Регистрация'}</h2>

            <div className={login_s.tabs}>
              <button className={`${login_s.tab} ${!regButton ? login_s.tab_active : ''}`} onClick={() => setRegButton(false)}>Вход</button>
              <button className={`${login_s.tab} ${regButton ? login_s.tab_active : ''}`} onClick={() => setRegButton(true)}>Регистрация</button>
            </div>

            {!regButton ? (
              <div className={login_s.inputs_wrapper}>
                <label>Почта</label>
                <div className={`${login_s.input_wrapper} ${emptyFields.includes('login') ? login_s.input_error_red : ''}`}>
                  <input
                    type="text"
                    placeholder="Введите email"
                    ref={login_ref}
                    onKeyDown={handleKeyDown}
                    onChange={() => setEmptyFields(p => p.filter(f => f !== 'login'))}
                  />
                </div>

                <label>Пароль</label>
                <div className={`${login_s.input_wrapper} ${emptyFields.includes('password') ? login_s.input_error_red : ''}`}>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Пароль"
                    ref={password_ref}
                    onChange={() => setEmptyFields(p => p.filter(f => f !== 'password'))}
                  />
                  <button className={login_s.eye_btn} onClick={() => setShowPassword(p => !p)}>
                    {showPassword ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>

                <button className={login_s.forgot} onClick={() => setForgotStep(1)}>Забыли пароль?</button>

                {logError && <p className={login_s.error}>Неверный логин или пароль</p>}
                <button onClick={handleLogin} className={login_s.submit_but}>Войти</button>
              </div>
            ) : (
              <div className={login_s.inputs_wrapper}>
                <label>Почта</label>
                <div className={`${login_s.input_wrapper} ${emptyFields.includes('email') ? login_s.input_error_red : emailHint ? login_s.input_error : ''}`}>
                  <input
                    type="text"
                    placeholder="Введите email"
                    ref={email_ref}
                    onChange={e => { validateEmail(e.target.value); setEmptyFields(p => p.filter(f => f !== 'email')); }}
                  />
                </div>
                {emailHint && <p className={login_s.hint}>⚠ {emailHint}</p>}

                <label>Логин</label>
                <div className={`${login_s.input_wrapper} ${emptyFields.includes('username') ? login_s.input_error_red : usernameHint ? login_s.input_error : ''}`}>
                  <input
                    type="text"
                    placeholder="Логин"
                    ref={register_username_ref}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.code === 'Space') {
                        e.preventDefault();
                        return;
                      }
                      handleKeyDown(e);
                    }}
                    maxLength={32}
                    onChange={e => { validateUsername(e.target.value); setEmptyFields(p => p.filter(f => f !== 'username')); }}
                  />
                </div>
                {usernameHint && <p className={login_s.hint}>⚠ {usernameHint}</p>}

                <label>Пароль</label>
                <div className={`${login_s.input_wrapper} ${emptyFields.includes('reg_password') ? login_s.input_error_red : passwordHint ? login_s.input_error : ''}`}>
                  <input
                    type={showRegPassword ? "text" : "password"}
                    placeholder="Придумайте пароль"
                    ref={register_password_ref}
                    onChange={e => { validatePassword(e.target.value); setEmptyFields(p => p.filter(f => f !== 'reg_password')); }}
                  />
                  <button className={login_s.eye_btn} onClick={() => setShowRegPassword(p => !p)}>
                    {showRegPassword ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>

                <label>Повторите пароль</label>
                <div className={`${login_s.input_wrapper} ${emptyFields.includes('reg_password2') ? login_s.input_error_red : passwordHint ? login_s.input_error : ''}`}>
                  <input
                    type={showRegPassword2 ? "text" : "password"}
                    placeholder="Повторите пароль"
                    ref={register_password2_ref}
                    onChange={() => setEmptyFields(p => p.filter(f => f !== 'reg_password2'))}
                  />
                  <button className={login_s.eye_btn} onClick={() => setShowRegPassword2(p => !p)}>
                    {showRegPassword2 ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
                {passwordHint && <p className={login_s.error}>⚠ {passwordHint}</p>}
                {regError && <p className={login_s.error}>{regError}</p>}

                <button onClick={goToCode} className={login_s.submit_but}>Зарегистрироваться</button>
              </div>
            )}
          </>
        ) : (
          <>
            <button className={login_s.close_btn} onClick={closeLogin}>✕</button>
            <h2 className={login_s.title}>Подтверждение</h2>

            <p className={login_s.subtitle}>
              Код подтверждения отправлен на <b>{confirmEmail}</b>
            </p>

            <div className={login_s.inputs_wrapper}>
              <label>Код из письма</label>
              <div className={`${login_s.input_wrapper} ${codeError ? login_s.input_error_red : ''}`}>
                <input
                  type="text"
                  placeholder="Введите код"
                  ref={code_ref}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setCodeError('');
                  }}
                  maxLength={4}
                />
              </div>

              <button className={login_s.forgot} onClick={resendCode}>
                Отправить код повторно
              </button>

              {codeError && <p className={login_s.error}>⚠ {codeError}</p>}

              <button onClick={handleRegister} className={login_s.submit_but}>
                Подтвердить
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
